import React, { memo, useCallback, useMemo } from "react";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { isEqual } from "lodash-es";

import LocationHeader from "./LocationHeader";
import AdvanceSettingForm from "@/features/campaigns/shared/AdvanceSettingForm";
import PostcardCampaignLocationHeader from "@/features/campaigns/direct-mail/mail-shark/activeLocations/PostcardCampaignLocationHeader";
import { getDigitalCampaignLocationDisplayName } from "@/features/campaigns/digital/launch-digital-campaign/utils/locationDisplayName";
import {
  isTemporarilyClosedLocation,
  TemporaryClosedStatusNote,
} from "@/components/muiCustom/note/InlineStatusNote";

type LocationAdvancedSettingProps = {
  campaign?: Record<string, unknown>;
  loc: {
    id: string | number;
    location_name?: string;
    location_status?: string | number | null;
    radius?: string | number;
    reopening_date?: string | null;
    [key: string]: unknown;
  };
  locFormData?: Record<string, any>;
  expanded: Array<string | number>;
  setFieldValue: (
    _field: string | number,
    _value: Record<string, unknown>
  ) => void;
  setExpanded: (_expanded: Array<string | number>) => void;
  productList?: any[];
  getAdvanceSettingData?: {
    settings?: any[];
  };
  setFieldError?: unknown;
  handleOnChangeAdvancedSettingError?: (..._args: unknown[]) => void;
  isDigitalCampaign?: boolean;
};

const LocationAdvancedSetting = ({
  campaign,
  loc,
  locFormData = {},
  expanded,
  setFieldValue,
  setExpanded,
  productList,
  getAdvanceSettingData,
  handleOnChangeAdvancedSettingError,
  isDigitalCampaign = true,
}: LocationAdvancedSettingProps) => {
  const settingsData = getAdvanceSettingData?.settings;
  const handleOnExpand =
    (panel: string | number) =>
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      if (isExpanded) {
        setExpanded([...expanded, panel]);
      } else {
        setExpanded(expanded.filter((l) => l !== panel));
      }
    };

  const shouldExpandIt = useMemo(() => {
    return (id: string | number) => expanded.includes(id);
  }, [expanded]);

  const locName = useMemo(
    () => getDigitalCampaignLocationDisplayName(loc),
    [loc]
  );

  // to get advance settings data
  const handleOnFillSettingData = useCallback(
    (selectedSettingsData: unknown) => {
      if (isEqual(locFormData.advance_setting || [], selectedSettingsData)) {
        return;
      }

      const oldVal = { ...locFormData };
      setFieldValue(loc.id, {
        ...oldVal,
        advance_setting: selectedSettingsData,
      });
    },
    [setFieldValue, loc.id, locFormData]
  );

  return (
    <Accordion
      expanded={shouldExpandIt(loc.id)}
      onChange={handleOnExpand(loc.id)}
      className="location"
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1bh-content"
        id="panel1bh-header"
        className="location-header"
      >
        {isDigitalCampaign && (
          <LocationHeader
            locFormData={locFormData}
            locName={locName}
            campaign={campaign}
            hideMeta
          />
        )}

        {!isDigitalCampaign && (
          <PostcardCampaignLocationHeader
            locFormData={locFormData}
            locName={locName}
            campaign={campaign}
            locationStatusNote={
              isTemporarilyClosedLocation(loc) ? (
                <TemporaryClosedStatusNote location={loc} />
              ) : null
            }
          />
        )}
      </AccordionSummary>
      <AccordionDetails className="location-details">
        <div className="card w-100">
          <div className="card-body p-1 p-lg-auto">
            <AdvanceSettingForm
              settingsData={settingsData}
              onFillSettingsData={handleOnFillSettingData}
              productList={productList}
              initialAnswers={locFormData.advance_setting}
              locationName={loc?.location_name}
              isModal={false}
              handleOnChangeAdvancedSettingError={
                handleOnChangeAdvancedSettingError
              }
              locId={loc?.id}
              locationRadius={loc.radius}
            />
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

export default memo(LocationAdvancedSetting);
